<template>
  <div ref="container" :key="tableKey" :style="containerStyle">
    <virtual-scroll ref="virtualScroll" :data="displayedRows" row-key="Id" :item-size="baseItemHeight" key-prop="Id"
      @change="(virtualList) => tableData = virtualList">
      <template slot-scope="{ headerCellFixedStyle, cellFixedStyle }">
        <Table :data="tableData" style="width: 100%" size="small" stripe :show-summary="getSummariesFunc !== undefined"
          row-key="Id" border ref="Table" :headerCellStyle="headerCellFixedStyle" :cellStyle="cellFixedStyle"
          :summary-method="summaryMethod" :span-method="mergeCells" :row-class-name="rowClassName"
          :height="tableHeightProp" :max-height="tableMaxHeightProp" @header-dragend="onHeaderDragend"
          @cell-click="onCellClick">

          <!-- fallback slot for when no rows -->
          <template v-if="!loading" slot="empty">
            <slot name="empty-content">
              No rows
            </slot>
          </template>


          <!-- FIRST column: render group header text when row.__group -->
          <CustomColumn :config="firstColumn">

            <template v-slot:header>
              <slot :name="'header-' + firstColumn.prop" />
            </template>

            <template v-slot:content="{ row }">
              <template v-if="row.__group">
                <slot :name="'group-header'" :row="row" :collapseFunc="() => toggleGroup(row.__groupKey)">
                  {{ row.__groupKey }}
                </slot>
              </template>
              <template v-else>
                <slot :name="firstColumn.prop" :row="row" :isNewRow="row.__new === true" :clearNewRow="clearNewRow"
                  :isCellActive="isCellActive" :enterCell="enterCell" :commitCell="commitCell" :cancelCell="cancelCell"
                  :onNavigate="onCellNavigate" />
              </template>
            </template>

          </CustomColumn>

          <!-- All other columns: hide when group header -->
          <CustomColumn v-for="(column, index) in notFirstColumns" :key="column.prop" :config="column"
            :fill="index == notFirstColumns.length - 1 && !shownColumns.some(x => x.width === undefined)">

            <template v-slot:header>
              <template v-if="allowColumnConfig && column.prop === actionsColumnKey">
                <div style="height: 23px; position: relative; overflow: visible;">
                  <v-btn icon small @click="tableDialogTrigger++">
                    <v-icon size="20">mdi-application-cog</v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-else>
                <slot :name="'header-' + column.prop" />
              </template>
            </template>

            <template v-slot:content="{ row }">
              <!-- specifically show expand/collapse button for group rows -->
              <template v-if="row.__group && column.prop === actionsColumnKey">
                <v-btn icon @click.stop="toggleGroup(row.__groupKey)">
                  <v-icon size="20">
                    {{ collapsedGroups[row.__groupKey] ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                  </v-icon>
                </v-btn>
              </template>

              <!-- NEW ROW: default add button in Actions -->
              <template v-else-if="row.__new && column.prop === actionsColumnKey">
                <slot name="new-btn" :row="row" :doCreateNew="() => doCreateNew(row)">
                  <v-btn tile small class="px-0" color="primary" style="min-width: 30px;"
                    @click.stop="doCreateNew(row)">
                    <v-icon size="20">
                      mdi-plus
                    </v-icon>
                  </v-btn>
                </slot>
              </template>

              <!-- normal row: show content -->
              <template v-else-if="!row.__group">
                <slot :name="column.prop" :row="row" :isNewRow="row.__new === true" :clearNewRow="clearNewRow"
                  :isCellActive="isCellActive" :enterCell="enterCell" :commitCell="commitCell" :cancelCell="cancelCell"
                  :onNavigate="onCellNavigate">
                </slot>
              </template>
              <!-- else here is hidden content in a group row -->
            </template>

          </CustomColumn>

        </Table>
      </template>
    </virtual-scroll>

    <!-- Column configuration dialog -->
    <SlottedDialog :externaltrigger="tableDialogTrigger" :title="'Table column configuration'">
      <template v-slot:content>
        <v-container :key="tableDialogTrigger">
          <QAutoComplete v-model="shownColumns" :list="Object.values(columns).filter(col => col.show !== 'always')"
            multiple textkey="label" valuekey="prop" label="Select columns to display" externaledit clearable
            returnobject class="mb-2">
          </QAutoComplete>
          <v-btn @click="resetColumns">Reset changes</v-btn>
        </v-container>
      </template>
    </SlottedDialog>
  </div>
</template>


<script>
import VirtualScroll from 'el-table-virtual-scroll' //docs: https://xiaocheng555.github.io/el-table-virtual-scroll/#/VFixedDemo?code=VFixedDemo&codeTitle=%E5%9B%BA%E5%AE%9A%E5%88%97%EF%BC%88%E8%87%AA%E5%AE%9A%E4%B9%89%EF%BC%89
import { Table } from 'element-ui';
import CustomColumn from "./CustomColumn.vue";
import { debounce } from "debounce";
import SlottedDialog from "@/views/layouts/SlottedDialog.vue";

const deepClone = obj => JSON.parse(JSON.stringify(obj));

export default {
  name: "UpgradedTable",
  props: {
    tableStorageKey: String,      //used to store config changes
    height: [Number, String],     // fixed height (px or CSS string). If set -> lock.
    minHeight: [Number, String],  // optional
    maxHeight: [Number, String],  // if set (and height is unset) -> grow up to this
    baseItemHeight: {
      type: Number,
      default: 50
    },

    defaultColumns: {
      type: Object,
      required: true
    },
    groupBy: {
      type: String,
      default: null               //null here means dont group by
    },
    allowColumnConfig: Boolean,
    getSummariesFunc: Function,   //used for displaying summary row

    loading: Boolean,
    rows: Array,
    error: Object,

    showNewRow: { type: Boolean, default: false },
    createRowFunc: Function,
    newRowInitial: { type: Object, default: () => ({}) }, // starting model for the placeholder row

    // Optional: parent can decide per-row/per-column editability.
    isEditableCellFunc: { type: Function, default: null },
  },
  components: {
    Table,
    CustomColumn,
    VirtualScroll,
    SlottedDialog,
  },
  mixins: [],

  data() {
    return {
      tableKey: 1,                //key to refresh table
      tableData: [],              //virtualized items for scroll
      collapsedGroups: {},        // << reactive map: { [groupKey]: true }
      tableDialogTrigger: 1,      //trigger to open column configuration dialog
      actionsColumnKey: 'Actions',
      persistedColumns: {},
      defaultsSnapshot: {},
      autoHiddenByGroup: {},      // { [columnKey]: true } columns we hid automatically
      newRow: {                   // live model bound to the placeholder row cells
        Id: '__new__',
        __new: true,
        ...deepClone(this.newRowInitial)
      },

      editingCells: Object.create(null), // {"<rowId>.<prop>": true}
    };
  },

  created() {
    // freeze a clean copy of defaults so "reset" always has something pure
    this.newRow = { Id: '__new__', __new: true, ...deepClone(this.newRowInitial) };
    this.defaultsSnapshot = deepClone(this.defaultColumns);
    Object.freeze(this.defaultsSnapshot);

    if (this.tableStorageKey) {
      const stored = localStorage.getItem(this.tableStorageKey);
      this.persistedColumns = stored ? JSON.parse(stored) : {};
      // If the grouping is active at mount, ensure the grouped column isn't *persistently* hidden
      if (this.groupBy) {
        for (const k of this.getColumnKeysByProp(this.groupBy)) {
          if (this.persistedColumns[k]?.show === false) {
            const copy = { ...this.persistedColumns[k] };
            delete copy.show; // fall back to default
            this.$set(this.persistedColumns, k, Object.keys(copy).length ? copy : undefined);
          }
        }
      }
    }
  },

  mounted() {
    if (typeof ResizeObserver !== 'undefined' && this.$refs.container) {
      let pending = false;
      const run = () => {
        pending = false;
        const vs = this.$refs.virtualScroll;
        vs && vs.doHeaderLayout && vs.doHeaderLayout();
        vs && vs.doLayout && vs.doLayout();
        this.$refs.Table && this.$refs.Table.doLayout && this.$refs.Table.doLayout();
      };
      this._ro = new ResizeObserver(() => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(run);
      });
      this._ro.observe(this.$refs.container);
    }

    // Ensure group-by visibility logic wins even if persisted cols changed layout
    this.$nextTick(() => this.applyGroupByColumnVisibility(this.groupBy, null));
  },

  beforeDestroy() {
    this._ro && this._ro.disconnect && this._ro.disconnect();
  },

  methods: {
    findRow(rowId) {
      if (rowId === '__new__') return this.newRow || null;
      // Only normal rows (skip group headers)
      const rows = this.rowListNonGroups();
      return rows.find(r => r.Id === rowId) || null;
    },

    cellKey(rowId, prop) {
      return `${rowId}.${prop}`;
    },

    isCellActive: function (rowId, prop) {
      return !!this.editingCells[this.cellKey(rowId, prop)];
    },

    enterCell(rowId, prop) {
      const row = this.findRow(rowId);
      row.__snap = row.__snap || {};
      // shallow copy is fine for scalars; deep clone if needed
      row.__snap[prop] = row[prop];
      // mark "editing" if you use it for styling
      row.editing = true;
      // mark this cell as active so EditableCell shows the editor
      this.$set(this.editingCells, this.cellKey(rowId, prop), true);
    },

    commitCell(rowId, prop) {
      const row = this.findRow(rowId);
      if (row?.__snap) delete row.__snap[prop];
      row.editing = false;
      this.$delete(this.editingCells, this.cellKey(rowId, prop));
    },

    cancelCell(rowId, prop) {
      const row = this.findRow(rowId);
      if (row && row.__snap && Object.prototype.hasOwnProperty.call(row.__snap, prop)) {
        // restore original
        this.$set(row, prop, row.__snap[prop]);
      }
      row.dirty = false;
      row.editing = false;
      this.$delete(this.editingCells, this.cellKey(rowId, prop));
    },

    onCellClick(row, column, cell, event) {
      const prop = column && column.property;
      if (!row || row.__group || !prop) return;

      // don't start edit when clicking a button etc.
      const interactive = event?.target?.closest('button, a, [role="button"], .v-btn');
      if (interactive) return;

      // ensure only one editor at a time
      this.clearAllEditing();

      if (this.isEditable(row, prop)) this.enterCell(row.Id, prop);
    },

    isEditable(row, prop) {
      if (!row || row.__group || !prop) return false;
      if (typeof this.isEditableCellFunc === 'function') {
        try { return !!this.isEditableCellFunc(row, prop); }
        catch (e) { console.warn('isEditableCellFunc threw', e); return false; }
      }
      // Was excluding Actions; now allow it:
      const readOnly = new Set(['TotalAmount']);
      if (readOnly.has(prop)) return false;
      if (prop === 'ArticleNumber') return !!row.__new;
      return true;
    },


    visibleProps() {
      // In visual order, exclude group headers and Actions, and hidden cols
      const skip = new Set([this.actionsColumnKey, 'HiddenNoInvoice', 'NoDetailInvoice']); // toggles
      return Object.values(this.columns)
        .filter(c => c && c.show !== false && c.prop && !skip.has(c.prop))
        .map(c => c.prop);
    },

    rowListNonGroups() {
      // Only real rows (skip group header rows)
      return (this.displayedRows || []).filter(r => !r.__group);
    },

    findRowCol(rowId, prop) {
      const rows = this.rowListNonGroups();
      const r = rows.findIndex(x => x.Id === rowId);
      const cols = this.visibleProps();
      const c = cols.indexOf(prop);
      return { r, c, rows, cols };
    },

    clamp(v, min, max) { return Math.max(min, Math.min(max, v)); },

    scrollTo(rIndex) {
      // Basic scroll using known item height
      const container = this.$refs.container;
      if (!container || typeof this.baseItemHeight !== 'number') return;
      const y = this.baseItemHeight * rIndex;
      const half = container.clientHeight / 2;
      container.scrollTop = Math.max(0, y - half);
      // also ask virtual scroll to relayout
      this.$refs.virtualScroll && this.$refs.virtualScroll.doLayout && this.$refs.virtualScroll.doLayout();
    },

    clearAllEditing() {
      // optional: keep a single active cell
      this.editingCells = Object.create(null);
    },

    openCell(rowId, prop, { keepOthers = false } = {}) {
      if (!keepOthers) this.clearAllEditing();
      this.enterCell(rowId, prop);
    },

    navigateFrom(rowId, prop, intent) {
      const { r, c, rows, cols } = this.findRowCol(rowId, prop);
      if (r < 0 || c < 0) return;

      let nr = r, nc = c;
      if (intent.kind === 'up') nr = r - 1;
      if (intent.kind === 'down') nr = r + 1;
      if (intent.kind === 'left') nc = c - 1;
      if (intent.kind === 'right') nc = c + 1;

      // wrap horizontally like spreadsheets
      if (intent.kind === 'right' && nc >= cols.length) { nc = 0; nr = r + 1; }
      if (intent.kind === 'left' && nc < 0) { nc = cols.length - 1; nr = r - 1; }

      nr = this.clamp(nr, 0, rows.length - 1);
      nc = this.clamp(nc, 0, cols.length - 1);

      // Skip non-editable cells; bound attempts to avoid infinite loops
      let attempts = 0;
      const maxAttempts = Math.max(1, rows.length * Math.max(1, cols.length));
      while (attempts < maxAttempts) {
        const candidateRow = rows[nr];
        const candidateProp = cols[nc];
        if (candidateRow && candidateProp && this.isEditable(candidateRow, candidateProp)) {
          // Commit source if required
          if (intent.commit) this.$emit('cell-commit', { rowId, prop });
          this.openCell(candidateRow.Id, candidateProp);
          this.$nextTick(() => this.scrollTo(nr));
          return;
        }
        // advance again in the same direction
        if (intent.kind === 'up') nr -= 1;
        if (intent.kind === 'down') nr += 1;
        if (intent.kind === 'left') nc -= 1;
        if (intent.kind === 'right') nc += 1;
        // wrap
        if (intent.kind === 'right' && nc >= cols.length) { nc = 0; nr += 1; }
        if (intent.kind === 'left' && nc < 0) { nc = cols.length - 1; nr -= 1; }
        // clamp within bounds
        nr = this.clamp(nr, 0, rows.length - 1);
        nc = this.clamp(nc, 0, cols.length - 1);
        attempts++;
      }
      // If we get here, nothing editable found; just close current edit if any
      if (intent.commit) this.$emit('cell-commit', { rowId, prop });
    },

    onCellNavigate(rowId, prop, intent) {
      this.navigateFrom(rowId, prop, intent);
    },

    u(v) { return typeof v === 'number' ? `${v}px` : v; }, // unit helper

    isAnyEditing() {
      const r = (x) => x && (x.editing || x.focus || x.dirty);
      return (this.rows || []).some(r) || r(this.newRow);
    },

    onHeaderDragend(newWidth, oldWidth, column) {
      const k = column.property;
      const base = this.persistedColumns[k] || {};
      // write into persistedColumns only
      this.$set(this.persistedColumns, k, { ...base, width: newWidth });
      this.$nextTick(() => this.$refs.virtualScroll.doHeaderLayout());
    },

    debounceSaveColumnConfig: debounce(async function () {
      this.saveColumnConfig();
    }, 700),

    async saveColumnConfig() {
      if (!this.tableStorageKey) return;
      localStorage.setItem(this.tableStorageKey, JSON.stringify(this.persistedColumns));
    },

    resetColumns() {
      if (this.tableStorageKey) localStorage.removeItem(this.tableStorageKey);
      this.persistedColumns = {};     // back to frozen defaults
      this.tableKey++;                // remount the heavy components
      this.$nextTick(() => {
        const vs = this.$refs.virtualScroll;
        vs && vs.doHeaderLayout && vs.doHeaderLayout();
        vs && vs.doLayout && vs.doLayout();
        vs && vs.reset && vs.reset();
        this.$refs.Table && this.$refs.Table.doLayout && this.$refs.Table.doLayout();
      });
    },

    async doCreateNew(row) {
      const copy = deepClone(row);// send a clean copy
      await this.createRowFunc(copy);
      this.clearNewRow();   // reset the model
    },

    clearNewRow() {
      this.newRow = { Id: '__new__', __new: true, ...deepClone(this.newRowInitial) };
    },

    isGrouped() {
      return !!this.groupBy;
    },

    toggleGroup(key) {
      if (this.collapsedGroups[key]) {
        // expand
        this.$delete(this.collapsedGroups, key);
      } else {
        // collapse
        this.$set(this.collapsedGroups, key, true);
      }

      // let virtual-scroll recompute sizes after row count changes
      this.$nextTick(() => {
        const vs = this.$refs.virtualScroll;
        if (vs && typeof vs.doLayout === 'function') vs.doLayout();
        if (vs && typeof vs.reset === 'function') vs.reset(); // some versions expose reset()
      });
    },

    mergeCells({ row, column, columnIndex }) {
      if (!this.isGrouped()) return [1, 1];

      if (row && row.__group) {
        // keep Actions cell normal
        if (column && column.property === this.actionsColumnKey) return [1, 1];

        // make the first visible cell span across columns
        if (columnIndex === 0) {
          // number of visible, non-Actions columns in this (non-fixed) segment
          const maxSpan = this._visibleNonActionCount;
          // force 11 but clamp so we don't overshoot when cols are hidden
          return [1, Math.min(11, maxSpan)];
        }
        return [0, 0];
      }
      return [1, 1];
    },

    rowClassName({ row }) {
      if (!this.isGrouped()) return '';
      return row && row.__group ? 'group-header-row' : '';
    },

    getColumnKeysByProp(prop) {
      if (!prop) return [];
      return Object.keys(this.columns).filter(k => this.columns[k]?.prop === prop);
    },

    applyGroupByColumnVisibility(newProp, oldProp) {
      // 1) Unhide the previously auto-hidden column(s)
      if (oldProp) {
        for (const k of this.getColumnKeysByProp(oldProp)) {
          if (this.autoHiddenByGroup[k]) this.$delete(this.autoHiddenByGroup, k);
        }
      }

      // 2) Hide the new grouping column(s) (unless 'always' or actions)
      if (newProp) {
        for (const k of this.getColumnKeysByProp(newProp)) {
          if (newProp === this.actionsColumnKey) continue;                // never hide actions
          const defShow = this.defaultsSnapshot[k]?.show;
          if (defShow === 'always') continue;                              // respect "always"
          this.$set(this.autoHiddenByGroup, k, true);
        }
      }

      // 3) Re-layout after structure change
      this.$nextTick(() => {
        const vs = this.$refs.virtualScroll;
        vs && vs.doHeaderLayout && vs.doHeaderLayout();
        vs && vs.doLayout && vs.doLayout();
        this.$refs.Table && this.$refs.Table.doLayout && this.$refs.Table.doLayout();
      });
    },

    // Make sure summaries ignore the new row
    summaryMethod(param) {
      if (!this.getSummariesFunc) {
        return undefined;
      }
      const data = (param?.data || []).filter(r => !r.__new);
      return this.getSummariesFunc({ ...param, data });
    },
  },

  computed: {
    hasFixedHeight() {
      return this.height !== undefined && this.height !== null && this.height !== '';
    },

    containerStyle() {
      // mirror what you pass to <el-table>
      const s = { overflow: 'auto' };
      if (this.hasFixedHeight) {
        s.height = this.u(this.height);
      } else {
        s.height = 'auto';
        if (this.minHeight != null) s.minHeight = this.u(this.minHeight);
        if (this.maxHeight != null) s.maxHeight = this.u(this.maxHeight);
      }
      return s;
    },

    tableHeightProp() {
      // number or string; undefined means “don’t set”
      return this.hasFixedHeight ? this.height : undefined;
    },

    tableMaxHeightProp() {
      return !this.hasFixedHeight && this.maxHeight != null ? this.maxHeight : undefined;
    },

    firstColumn() {
      return Object.values(this.columns).find(c => c.show) || null;
    },

    notFirstColumns() {
      return Object.values(this.columns).filter(c => c.show !== false && c !== this.firstColumn);
    },

    columns() {
      const out = {};
      const base = this.defaultsSnapshot;
      // include defaults
      for (const k of Object.keys(base)) out[k] = deepClone(base[k]);
      // overlay persisted values
      for (const k of Object.keys(this.persistedColumns)) {
        const persisted = deepClone(this.persistedColumns[k]);
        // Never let persisted config override runtime labels (keeps i18n working)
        if (persisted && Object.prototype.hasOwnProperty.call(persisted, 'label')) {
          delete persisted.label;
        }
        out[k] = { ...(out[k] || {}), ...persisted };
      }
      // Runtime overlay: if auto-hidden due to current grouping, force hide (except 'always')
      for (const k of Object.keys(out)) {
        if (this.autoHiddenByGroup[k] && out[k]?.show !== 'always') {
          out[k].show = false;
        }
      }
      return out;
    },

    shownColumns: {
      get() {
        return Object.values(this.columns).filter(c => c.show);
      },
      set(value) {
        const selected = new Set(value.map(c => c.prop));
        Object.keys(this.columns).forEach((k) => {
          const col = this.columns[k];
          const desired = col.show === 'always' ? 'always' : selected.has(k);
          const base = this.persistedColumns[k] || {};
          this.$set(this.persistedColumns, k, { ...base, show: desired });
        });
        this.$nextTick(() => this.$refs.virtualScroll.doHeaderLayout());
      }
    },

    _visibleColumnCount() {
      return Object.values(this.columns).filter(c => c && c.show !== false).length || 1;
    },

    _visibleNonActionCount() {
      return Object.values(this.columns)
        .filter(c => c && c.show !== false && c.prop !== this.actionsColumnKey).length || 1;
    },

    displayedRows() {
      var out = this.groupBy ? [] : (this.rows || []);

      if (this.groupBy && this.rows && this.rows.length > 0) {
        const by = this.groupBy;
        const groups = new Map();

        for (const r of this.rows) {
          let key = r[by];
          if (by.toLowerCase().includes('date') || by.toLowerCase().includes('time')) {
            const d = new Date(r[by]);
            key = isNaN(d)
              ? r[by]
              : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          }
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(r);
        }

        for (const [key, rows] of groups.entries()) {
          out.push({
            Id: `group-${key}`,
            __group: true,
            __groupKey: key,
            _groupRows: rows
          });

          // Hide group rows if collapsed
          if (!this.collapsedGroups[key]) {
            out.push(...rows);
          }
        }
      }

      if (this.showNewRow) {
        out = out.slice(); // avoid mutating original
        out.push(this.newRow)
      }

      return out;
    },
  },

  watch: {
    persistedColumns: {
      deep: true,
      handler() { this.debounceSaveColumnConfig(); }
    },

    groupBy: {
      immediate: true,
      handler(newVal, oldVal) {
        this.collapsedGroups = {};            // keep your existing reset
        this.applyGroupByColumnVisibility(newVal, oldVal);
      }
    },

    newRowInitial: {
      deep: true,
      handler(v) {
        this.newRow = { Id: '__new__', __new: true, ...deepClone(v || {}) };
      }
    }
  },
};
</script>


<style lang='scss'>
.el-table--small .el-table__cell {
  padding: 0px 0;
}

.el-table__body td.el-table__cell {
  border-right: 1px solid transparent !important;
}

/* make group headers stand out */
.group-header-row .el-table__cell {
  font-weight: 600;
  background: #f6f7f9;
  border-top: 1px solid #e6e8eb;
  border-bottom: 1px solid #e6e8eb;
}

.group-toggle-btn {
  margin-right: 2px;
}

/* keep your existing override */
.el-table__body td.el-table__cell {
  border-right: 1px solid transparent !important;
}

.el-table__body td.is-right .editable-cell__display,
.el-table__body td.is-right .editable-cell__editor {
  justify-content: flex-end;
  text-align: right;
}

.el-table__body td.is-center .editable-cell__display,
.el-table__body td.is-center .editable-cell__editor {
  justify-content: center;
  text-align: center;
}
</style>